import { useState, useEffect, useRef, useCallback } from "react";
import { Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

const MAX_RECORDING_SECONDS = 180; // 3 minutes

// Detect mobile Safari / iOS — webkitSpeechRecognition behaves differently there
const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const VoiceButton = ({ onTranscript, isLoading, onRecordingStart, voiceMode, onToggleVoiceMode }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [elapsed, setElapsed] = useState(0);

  const recognitionRef = useRef(null);
  const isActiveRef = useRef(false);
  const finalizedTextRef = useRef("");
  const interimTextRef = useRef("");
  const retryCountRef = useRef(0);
  const timerRef = useRef(null);
  const maxTimerRef = useRef(null);
  const SpeechRecRef = useRef(null);

  // Initialize speech recognition engine
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setIsSupported(false);
      return;
    }
    SpeechRecRef.current = SR;
  }, []);

  const createRecognition = useCallback(() => {
    const SR = SpeechRecRef.current;
    if (!SR) return null;

    const recognition = new SR();
    // On iOS Safari, continuous=true is unreliable — use false and rely on onend restart.
    recognition.continuous = !isIOS;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let newFinals = "";
      let interim = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          newFinals += event.results[i][0].transcript + " ";
        } else {
          interim += event.results[i][0].transcript;
        }
      }

      if (newFinals) {
        finalizedTextRef.current += newFinals;
        retryCountRef.current = 0;
      }
      interimTextRef.current = interim;

      const full = (finalizedTextRef.current + interim).trim();
      setLiveTranscript(full);
      onTranscript?.(full, false);
    };

    recognition.onerror = (event) => {
      const err = event.error;
      if (err === "not-allowed" || err === "service-not-allowed") {
        toast.error("Microphone access denied. Please allow mic access in your browser settings.");
        isActiveRef.current = false;
        setIsRecording(false);
        cleanup();
        return;
      }
      if (err === "audio-capture") {
        toast.error("No microphone found. Please connect a mic and try again.");
        isActiveRef.current = false;
        setIsRecording(false);
        cleanup();
        return;
      }
      if (err === "network") {
        // Network blip — let onend restart us silently. Only surface if it persists.
        if (retryCountRef.current > 5) {
          toast.error("Speech service unreachable. Check your internet connection.");
        }
        return;
      }
      if (err === "language-not-supported") {
        toast.error("Speech language not supported on this device.");
        isActiveRef.current = false;
        setIsRecording(false);
        cleanup();
        return;
      }
      // no-speech / aborted — let onend handle restart silently
    };

    recognition.onend = () => {
      // Persist any interim text that wasn't finalized before the session ended
      if (interimTextRef.current && interimTextRef.current.trim()) {
        finalizedTextRef.current += interimTextRef.current.trim() + " ";
        interimTextRef.current = "";
      }

      if (isActiveRef.current) {
        retryCountRef.current += 1;
        if (retryCountRef.current > 80) {
          toast.error("Voice recognition stopped unexpectedly. Tap mic to try again.");
          isActiveRef.current = false;
          setIsRecording(false);
          cleanup();
          return;
        }
        // Recreate and restart
        try {
          const newRec = createNewSession();
          if (newRec) {
            recognitionRef.current = newRec;
            newRec.start();
          }
        } catch {
          setTimeout(() => {
            if (isActiveRef.current) {
              try {
                const newRec = createNewSession();
                if (newRec) {
                  recognitionRef.current = newRec;
                  newRec.start();
                }
              } catch {
                isActiveRef.current = false;
                setIsRecording(false);
                cleanup();
              }
            }
          }, 200);
        }
      }
    };

    return recognition;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createNewSession = useCallback(() => {
    return createRecognition();
  }, [createRecognition]);

  const cleanup = useCallback(() => {
    clearInterval(timerRef.current);
    clearTimeout(maxTimerRef.current);
    setElapsed(0);
  }, []);

  const startRecording = useCallback(async () => {
    if (!SpeechRecRef.current || isLoading || isRecording) return;

    // Step 1 — Check current mic permission via the Permissions API.
    // Only show the OS prompt when state is "prompt" (first time) or unknown.
    // If "granted", skip getUserMedia entirely so users aren't re-prompted.
    let needsPermissionPrompt = true;
    try {
      if (navigator.permissions?.query) {
        const status = await navigator.permissions.query({ name: "microphone" });
        if (status.state === "granted") {
          needsPermissionPrompt = false;
        } else if (status.state === "denied") {
          toast.error("Microphone access is blocked. Enable it in your browser settings (lock icon in the address bar).");
          return;
        }
      }
    } catch {
      // Permissions API not supported (Safari, older browsers) — fall through to getUserMedia
    }

    if (needsPermissionPrompt && navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Release the stream — we only needed the permission grant
        stream.getTracks().forEach((t) => t.stop());
      } catch (permErr) {
        console.error("Mic permission error:", permErr);
        if (permErr.name === "NotAllowedError" || permErr.name === "SecurityError") {
          toast.error("Microphone permission denied. Please enable mic access in your browser settings.");
        } else if (permErr.name === "NotFoundError" || permErr.name === "OverconstrainedError") {
          toast.error("No microphone detected on this device.");
        } else if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
          toast.error("Voice input requires a secure (HTTPS) connection.");
        } else {
          toast.error(`Microphone error: ${permErr.message || permErr.name}`);
        }
        return;
      }
    }

    try {
      finalizedTextRef.current = "";
      interimTextRef.current = "";
      retryCountRef.current = 0;
      setLiveTranscript("");
      setElapsed(0);
      isActiveRef.current = true;
      setIsRecording(true);

      const recognition = createRecognition();
      if (!recognition) {
        toast.error("Speech recognition not available in this browser. Try Chrome or Edge.");
        setIsRecording(false);
        return;
      }
      recognitionRef.current = recognition;
      recognition.start();
      onRecordingStart?.();

      timerRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);

      maxTimerRef.current = setTimeout(() => {
        if (isActiveRef.current) {
          stopRecording();
          toast.info("Recording stopped — 3 minute limit reached. You can edit and send now.");
        }
      }, MAX_RECORDING_SECONDS * 1000);
    } catch (error) {
      setIsRecording(false);
      isActiveRef.current = false;
      console.error("startRecording error:", error);
      toast.error(`Failed to start microphone: ${error?.message || "Unknown error"}`);
    }
  }, [isLoading, isRecording, createRecognition, onRecordingStart]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRecording = useCallback(() => {
    if (!isRecording) return;

    isActiveRef.current = false;
    cleanup();

    // Capture any unfinalized interim text before tearing down
    if (interimTextRef.current && interimTextRef.current.trim()) {
      finalizedTextRef.current += interimTextRef.current.trim() + " ";
      interimTextRef.current = "";
    }

    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
    try { recognitionRef.current?.abort(); } catch { /* ignore */ }
    setIsRecording(false);

    const finalText = finalizedTextRef.current.trim() || liveTranscript.trim();
    if (finalText) {
      onTranscript?.(finalText, true);
    }

    setLiveTranscript("");
    finalizedTextRef.current = "";
  }, [isRecording, liveTranscript, onTranscript, cleanup]);

  // Auto-stop when AI starts processing
  useEffect(() => {
    if (isLoading && isRecording) {
      stopRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isActiveRef.current = false;
      cleanup();
      try { recognitionRef.current?.abort(); } catch {}
    };
  }, [cleanup]);

  if (!isSupported) return null;

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const remaining = MAX_RECORDING_SECONDS - elapsed;

  return (
    <div className="flex flex-col items-center gap-2 w-full">
      {/* Live transcript preview while recording — sized for 4-5 lines to avoid early scrolling */}
      {isRecording && liveTranscript && (
        <div
          className="w-full bg-[#E5D4C1]/30 border border-[#D4C9B8]/40 rounded-2xl px-4 py-3 text-sm font-sans text-[#4A5D4A] min-h-[7.5rem] max-h-48 overflow-y-auto leading-relaxed"
          data-testid="live-transcript"
        >
          {liveTranscript}
          <span className="inline-block w-0.5 h-4 bg-[#C67B5C] ml-0.5 animate-pulse align-text-bottom" />
        </div>
      )}

      <div className="flex items-center gap-3">
        {/* Speaker toggle — mutes/unmutes AI TTS playback. Sits immediately LEFT of the mic. */}
        {onToggleVoiceMode && (
          <button
            onClick={onToggleVoiceMode}
            disabled={isLoading}
            className={`relative p-3 rounded-full transition-all duration-300 ${
              voiceMode
                ? "bg-[#E5D4C1] text-[#4A5D4A] hover:bg-[#D4C9B8]"
                : "bg-[#7A8A7A]/20 text-[#7A8A7A] hover:bg-[#7A8A7A]/30 ring-2 ring-[#7A8A7A]/40"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            data-testid="tts-toggle-button"
            aria-pressed={voiceMode}
            title={voiceMode ? "AI voice is ON — tap to mute (read-only mode)" : "AI voice is MUTED — tap to enable"}
          >
            {voiceMode ? (
              <Volume2 className="w-5 h-5" />
            ) : (
              <VolumeX className="w-5 h-5" />
            )}
            {!voiceMode && (
              <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] font-sans text-[#7A8A7A] whitespace-nowrap">
                Muted
              </span>
            )}
          </button>
        )}

        <button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isLoading}
          className={`relative p-4 rounded-full transition-all duration-300 ${
            isRecording
              ? "bg-[#C67B5C] text-white scale-110 shadow-lg shadow-[#C67B5C]/30"
              : "bg-[#7A9B7E] text-white hover:bg-[#6A8B6E] hover:scale-105 shadow-md"
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid="voice-input-button"
          title={isRecording ? "Stop & review before sending" : "Start speaking"}
        >
          {isRecording && (
            <span className="absolute inset-0 rounded-full bg-[#C67B5C] animate-ping opacity-40" />
          )}
          {isRecording ? (
            <MicOff className="w-5 h-5 relative z-10" />
          ) : (
            <Mic className="w-6 h-6 relative z-10" />
          )}
        </button>

        <div className="flex flex-col">
          <span className={`text-xs font-sans ${isRecording ? "text-[#C67B5C]" : "text-[#8A7A6A]"}`}>
            {isRecording ? "Listening... tap to stop & review" : "Tap to speak"}
          </span>
          {isRecording && (
            <span className={`text-xs font-sans tabular-nums ${remaining <= 30 ? "text-[#C67B5C]" : "text-[#7A8A7A]"}`} data-testid="recording-timer">
              {formatTime(elapsed)} / {formatTime(MAX_RECORDING_SECONDS)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceButton;
